import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { SelfcareService } from '@yol-digital/ms-client';
import { CookieService } from 'ngx-cookie-service';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { ENVIRONMENT_URLS_CONFIG_TOKEN } from 'app-initialization';
import { FeatureFlagService } from 'feature-flag';
import { ToastService } from 'toast';
import { TranslateService } from 'translate';
import { ObservableCachedDataLoaderService, randomString, StorageKeys, StorageService } from 'utils';
import { AuthService } from './auth.service';
import Api = SelfcareService.Api;

type AccountResp = SelfcareService.AccountResp;
type SubscriptionsResp = SelfcareService.SubscriptionsResp;
type SubscriptionResp = SelfcareService.SubscriptionResp;

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private storageService = inject(StorageService);
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private cookieService = inject(CookieService);
  private cachedDataLoader = inject(ObservableCachedDataLoaderService);
  private translateService = inject(TranslateService);
  private toasts = inject(ToastService);
  private featureFlagService = inject(FeatureFlagService);
  private config = inject(ENVIRONMENT_URLS_CONFIG_TOKEN);
  private platformId = inject(PLATFORM_ID);
  private api: Api;

  constructor() {
    this.api = new Api(this.config.newMicroServiceEndpoint, this.http);
  }

  public setVisitorId() {
    const visitorId = randomString(24);
    this.storageService.set(StorageKeys.VisitorId, visitorId);
  }

  public getVisitorId() {
    return this.storageService.get<string>(StorageKeys.VisitorId);
  }

  public getAccount(): Observable<AccountResp | null> {
    return this.isAuthenticated().pipe(
      switchMap(isAuthenticated => {
        if (!isAuthenticated) return of(null);
        return this.cachedDataLoader.get('account', () => {
          return this.api.public.account().pipe(
            map(account => {
              if ('error' in account) throw new Error(account.error);
              this.featureFlagService.setAttributeOverrides({ accountId: account.accountId });
              return account;
            }),
            catchError(err => {
              if (!err?.wasCaught) {
                console.error(err);
                this.toasts.add(this.translateService.getTranslation(['server_error']), false);
              }
              return of(null);
            })
          );
        });
      })
    );
  }

  public removeAccountCachedResponse() {
    this.cachedDataLoader.remove('account');
  }

  public setCustomerAddress(key: StorageKeys) {
    return this.getAccount().pipe(
      tap(account => {
        const billingAddress = account?.billingAddress;
        if (billingAddress) {
          this.storageService.set(key, {
            postCode: billingAddress.zip,
            city: billingAddress.city,
            streetName: billingAddress.street,
            streetNumber: billingAddress.streetNumber,
            ...(billingAddress.co ? { co: billingAddress.co } : {}),
          });
        }
      })
    );
  }

  public getSubscriptions(): Observable<SubscriptionsResp[]> {
    return this.isAuthenticated().pipe(
      switchMap(isAuthenticated => {
        if (!isAuthenticated) return of([]);
        return this.api.public.subscriptions().pipe(
          map(subscriptions => {
            if ('error' in subscriptions) throw new Error(subscriptions.error);
            return subscriptions.filter(sub => sub.subscriptionStatus !== 'terminated');
          }),
          catchError(err => {
            if (!err?.wasCaught) {
              console.error(err);
              this.toasts.add(this.translateService.getTranslation(['server_error']), false);
            }
            return of([]);
          })
        );
      })
    );
  }

  public getSubscriptionById(id: string): Observable<SubscriptionResp | null> {
    return this.isAuthenticated().pipe(
      switchMap(isAuthenticated => {
        if (!isAuthenticated) return of(null);
        return this.cachedDataLoader.get(`subscription_${id}`, () =>
          this.api.public.subscription(id).pipe(
            map(subscription => {
              if ('error' in subscription) throw new Error(subscription.error);
              return subscription;
            })
          )
        );
      })
    );
  }

  public updateContactDetails(data: { contactNumber: string }) {
    return this.isAuthenticated().pipe(
      switchMap(isAuthenticated => {
        if (!isAuthenticated) return of(null);
        return this.api.public.updateContactDetails(data).pipe(
          tap(() => {
            this.cachedDataLoader.remove('account');
          })
        );
      })
    );
  }

  public updateUserEmail(newEmail: string): Observable<
    | {
        refId?: string | undefined;
      }
    | {
        error: string;
      }
  > {
    return this.isAuthenticated().pipe(
      switchMap(isAuthenticated => {
        if (!isAuthenticated) return of(null);
        return this.api.public.updateEmail({ newEmail });
      })
    );
  }

  public confirmEmail(code: string, refId: string) {
    return this.isAuthenticated().pipe(
      switchMap(isAuthenticated => {
        if (!isAuthenticated) return of(null);
        return this.api.public.confirmEmail({ refId, code });
      })
    );
  }

  public updateUserBillingAddress(data: SelfcareService.UpdateBillingAddressReq) {
    return this.isAuthenticated().pipe(
      switchMap(isAuthenticated => {
        if (!isAuthenticated) return of(null);
        return this.api.public.updateBillingAddress(data).pipe(
          tap(() => {
            this.cachedDataLoader.remove('account');
          })
        );
      })
    );
  }

  /**
   * Sets a the "previouslyLoggedInState" cookie which is used for detecting if a customer was logged in
   */
  private async setPreviouslyLoggedInCookie() {
    this.cookieService.set('previouslyLoggedInState', 'true', 90);
  }

  // Gets the "previouslyLoggedInState" cookie and converts it into a boolean
  public getPreviouslyLoggedInState(): boolean {
    const val = this.cookieService.get('previouslyLoggedInState');

    if (val) {
      return coerceBooleanProperty(val);
    }

    return false;
  }

  public isAuthenticated(): Observable<boolean> {
    if (!isPlatformBrowser(this.platformId)) return of(false); // Don't check login token on the server
    return this.authService.isAuthenticated().pipe(
      tap(loggedIn => {
        this.featureFlagService.setAttributeOverrides({ loggedIn });
        loggedIn && this.setPreviouslyLoggedInCookie();
      })
    );
  }

  public getUserType(): Observable<string> {
    return this.authService
      .isAuthenticated()
      .pipe(map(isAuthenticated => (isAuthenticated ? 'existing_user' : 'new_user_funnel')));
  }
}
